/**
 * The Selvedge band (owner's pick, 2026-10-17): the frayed edge of a cloth, warp threads of uneven thickness and
 * height with irregular gaps and a rare gold or rust thread. Like letters that have fused or worn away. Seeded, so
 * the same band comes back everywhere it is drawn: as an SVG on the web pages, as table cells in emails.
 * Redrawn after a design review (2026-10-17): the first draw read as a barcode (short rounded pills, too bright). Now
 * the threads are thin, nearly full height and square-ended, quieter, and only two or three are frayed short at one end.
 */

export interface Thread { x: number; y: number; w: number; h: number; color: string; opacity: number }
export interface BandPalette { base: string; soft: string; gold: string; rust: string }
export const NIGHT_BAND: BandPalette = { base: "#efe6d4", soft: "#c9bda6", gold: "#c89b4a", rust: "#b4482f" };
export const PAPER_BAND: BandPalette = { base: "#221c15", soft: "#4b4236", gold: "#a87b2c", rust: "#9a2f1d" };
/** The seed the owner chose on the studies canvas (seed 7, design A). */
export const BAND_SEED = 7 * 31 + 1;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function pick<T>(r: () => number, weighted: [T, number][]): T {
  let x = r() * weighted.reduce((a, w) => a + w[1], 0);
  for (const [v, w] of weighted) if ((x -= w) < 0) return v;
  return weighted[0]![0];
}

/** The threads of a band W×H. `snap` rounds to whole pixels (emails). */
export function selvedge(W: number, H: number, pal: BandPalette, seed = BAND_SEED, snap = false): Thread[] {
  const r = mulberry32(seed);
  const k = H / 12;
  const px = (v: number) => (snap ? Math.round(v) : v);
  const out: Thread[] = [];
  let x = 0;
  while (x < W) {
    const w = snap ? Math.max(1, Math.round(pick(r, [[1, 6], [1.5, 3], [2, 2]]) * k)) : pick(r, [[1, 6], [1.5, 3], [2, 2]]) * k;
    if (x + w > W) break;
    // Square-ended and nearly full height: each end wanders by up to a twelfth of the band.
    const top = px(r() * H / 12), bottom = px(H - r() * H / 12);
    const color = pick(r, [[pal.base, 10], [pal.soft, 5], [pal.gold, 0.8], [pal.rust, 0.4]]);
    const accent = color === pal.gold || color === pal.rust;
    const opacity = accent ? 0.6 + 0.2 * r() : 0.35 + 0.35 * r();
    out.push({ x, y: top, w, h: bottom - top, color, opacity });
    x += w + px(pick(r, [[1, 6], [2, 3], [3, 1.5], [5, 0.6]]) * k);
  }
  // The fray: two or three plain threads worn short, from the top or from the bottom.
  const plain = out.filter((t) => t.color === pal.base || t.color === pal.soft);
  for (let i = 0, frays = 2 + Math.floor(r() * 2); i < frays && plain.length; i++) {
    const t = plain.splice(Math.floor(r() * plain.length), 1)[0]!;
    const cut = px(t.h * (0.3 + 0.2 * r()));
    if (r() < 0.5) t.y += cut;
    t.h -= cut;
  }
  return out;
}

const n = (v: number) => Math.round(v * 100) / 100;
/** The band as an SVG (web pages; also written to public/brand by tools/brand.ts). */
export function selvedgeSvg(W: number, H: number, pal: BandPalette, seed = BAND_SEED): string {
  const rects = selvedge(W, H, pal, seed).map((t) => `<rect x="${n(t.x)}" y="${n(t.y)}" width="${n(t.w)}" height="${n(t.h)}" rx="${n(Math.min(0.3, t.w / 2))}" fill="${t.color}" fill-opacity="${n(t.opacity)}"/>`);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${rects.join("")}</svg>`;
}

/**
 * The band for emails: a one-row table, a cell per thread (a spacer down to the thread's top, then the thread) and per gap.
 * Images are often blocked in mail; cells always show. Opacity is mixed into the colour against the email's card.
 */
export function selvedgeEmail(W: number, H: number, pal: BandPalette, card: string, seed = BAND_SEED): string {
  const mix = (hex: string, over: string, a: number) => {
    const p = (h: string, i: number) => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
    return `#${[0, 1, 2].map((i) => Math.round(p(hex, i) * a + p(over, i) * (1 - a)).toString(16).padStart(2, "0")).join("")}`;
  };
  const cells: string[] = [];
  let x = 0;
  for (const t of selvedge(W, H, pal, seed, true)) {
    if (t.x > x) cells.push(`<td width="${t.x - x}" style="width:${t.x - x}px;font-size:0;line-height:0">&nbsp;</td>`);
    const spacer = t.y > 0 ? `<div style="height:${t.y}px;font-size:0;line-height:0">&nbsp;</div>` : "";
    cells.push(`<td width="${t.w}" valign="top" style="width:${t.w}px;height:${H}px;font-size:0;line-height:0;vertical-align:top">${spacer}<div style="width:${t.w}px;height:${t.h}px;background:${mix(t.color, card, t.opacity)};font-size:0;line-height:0">&nbsp;</div></td>`);
    x = t.x + t.w;
  }
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse"><tr>${cells.join("")}</tr></table>`;
}
